import { useEffect, useRef, useState } from 'react';

import {
  AssessmentOverdueDropdown,
  ChakraFlex,
  ChakraVStack,
  OverdueAssessmentMeta,
  SkeletonLoader,
  Text,
} from '@equip.health/ui';

import { AssessmentEvents } from '~/analytics';
import {
  AssessmentEventAttribute,
  AssessmentEventInteractionType,
  AssessmentEventSource,
} from '~/analytics/assessmentEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CategorizedAssessments } from '~/lib/types/survey/CategorizedAssessments';
import { collectOverdueAssessments } from '~/lib/util/assessmentIndexList.util';

const TOP_LEVEL_ID = 'survey-list-index';

const AssessmentIndexList = ({
  categorizedAssessments,
  isAssessmentTabUiRevampEnabled,
  isGetGroupedAssessmentsLoading,
  isScreenWidthLessThan1000px,
  selectedSurveyId,
  onSurveyClick,
}: {
  categorizedAssessments: CategorizedAssessments;
  isAssessmentTabUiRevampEnabled: boolean;
  isGetGroupedAssessmentsLoading: boolean;
  isScreenWidthLessThan1000px: boolean;
  selectedSurveyId: string;
  onSurveyClick: (id: string) => void;
}) => {
  const { trackEvent } = useAnalytics();

  const indexRef = useRef<HTMLDivElement | null>(null);

  const isCurrentSurveyIdSelected = (currentSurveyId: string) =>
    currentSurveyId === selectedSurveyId;
  const [overdueAssessments, setOverdueAssessments] = useState<
    OverdueAssessmentMeta[]
  >([]);

  useEffect(() => {
    const overdueAssessments = collectOverdueAssessments(
      categorizedAssessments,
    );
    setOverdueAssessments(overdueAssessments);
  }, [categorizedAssessments]);

  useEffect(() => {
    if (selectedSurveyId && indexRef.current) {
      const indexItem = indexRef.current.querySelector(
        `#${TOP_LEVEL_ID}__${selectedSurveyId}`,
      );
      if (indexItem) {
        indexItem.scrollIntoView({ behavior: 'auto', block: 'center' });
      }
    }
  }, [selectedSurveyId]);
  return (
    <ChakraFlex
      background="neutral.background.primary"
      display={isScreenWidthLessThan1000px ? 'none' : 'block'}
      flexDirection="column"
      id={TOP_LEVEL_ID}
      minWidth="244px"
      overflow="auto"
      padding={
        isAssessmentTabUiRevampEnabled ? '24px 16px 40px 16px' : '24px 0 40px 0'
      }
    >
      {isGetGroupedAssessmentsLoading && (
        <SkeletonLoader
          height="300px"
          id={TOP_LEVEL_ID}
          isLoaded={!isGetGroupedAssessmentsLoading}
        />
      )}
      <ChakraVStack alignItems="start" gap="18px" ref={indexRef} width="100%">
        {overdueAssessments.length > 0 && (
          <AssessmentOverdueDropdown
            onOverdueBtnClick={() => {
              trackEvent(AssessmentEvents.overdueIndexListButtonClicked, {
                [AssessmentEventAttribute.Source]:
                  AssessmentEventSource.AssessmentList,
              });
            }}
            onSurveyClick={(surveyExternalId) => {
              onSurveyClick(surveyExternalId);
            }}
            overdueAssessmentsMetaList={overdueAssessments}
          />
        )}
        {Object.entries(categorizedAssessments).map(
          ([categoryName, subCategories]) => (
            <ChakraFlex
              alignItems="start"
              flexDirection="column"
              key={categoryName}
              width="100%"
            >
              <Text
                color="neutral.primary"
                id={categoryName}
                paddingX={isAssessmentTabUiRevampEnabled ? '0px' : '16px'}
                variant="bodyMedium"
              >
                {categoryName}
              </Text>
              <ChakraVStack
                alignItems="start"
                gap="1.5rem"
                marginTop="16px"
                width="100%"
              >
                {Object.entries(subCategories).map(
                  ([subCategoryName, subCategoryData]) => (
                    <ChakraFlex
                      flexDirection="column"
                      key={subCategoryName}
                      width="100%"
                    >
                      <Text
                        color="neutral.secondary"
                        id={subCategoryName}
                        paddingX={
                          isAssessmentTabUiRevampEnabled ? '0px' : '16px'
                        }
                        variant="caption"
                      >
                        {subCategoryName}
                      </Text>
                      <ChakraVStack
                        alignItems="start"
                        gap="2px"
                        marginTop="8px"
                        width="100%"
                      >
                        {subCategoryData.assessments.map((survey) => (
                          <ChakraFlex
                            _hover={{
                              backgroundColor: 'secondary.10',
                              cursor: 'pointer',
                            }}
                            align="center"
                            background={
                              isCurrentSurveyIdSelected(survey.externalId)
                                ? 'secondary.10'
                                : 'neutral.background.primary'
                            }
                            borderRadius="6px"
                            data-testid={`${TOP_LEVEL_ID}__${survey.externalId}`}
                            height="26px"
                            id={`${TOP_LEVEL_ID}__${survey.externalId}`}
                            key={survey.externalId}
                            onClick={() => {
                              trackEvent(
                                AssessmentEvents.assessmentIndexMenuItemInteraction,
                                {
                                  [AssessmentEventAttribute.SurveyId]:
                                    survey.externalId,
                                  [AssessmentEventAttribute.SurveyName]:
                                    survey.name,
                                  [AssessmentEventAttribute.InteractionType]:
                                    AssessmentEventInteractionType.Click,
                                  [AssessmentEventAttribute.Source]:
                                    AssessmentEventSource.AssessmentIndexMenu,
                                },
                              );
                              onSurveyClick(survey.externalId);
                            }}
                            width="100%"
                          >
                            <Text
                              _hover={{
                                color: 'primary.90.variant',
                              }}
                              color={
                                isCurrentSurveyIdSelected(survey.externalId)
                                  ? 'primary.90.variant'
                                  : 'primary.100'
                              }
                              paddingX={
                                isAssessmentTabUiRevampEnabled ? '12px' : '16px'
                              }
                              variant={
                                isCurrentSurveyIdSelected(survey.externalId)
                                  ? 'captionMedium'
                                  : 'caption'
                              }
                              width="100%"
                            >
                              {survey.displayName}
                            </Text>
                          </ChakraFlex>
                        ))}
                      </ChakraVStack>
                    </ChakraFlex>
                  ),
                )}
              </ChakraVStack>
            </ChakraFlex>
          ),
        )}
      </ChakraVStack>
    </ChakraFlex>
  );
};

export default AssessmentIndexList;
